import * as React from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import {
  DefaultNavigatorOptions,
  ParamListBase,
  TabActionHelpers,
  TabNavigationState,
  TabRouter,
  TabRouterOptions,
  useNavigationBuilder,
  createNavigatorFactory
} from '@react-navigation/native';
import { ActivityIndicator } from 'react-native-paper';
import { layout } from 'src/theme/globalStyles';
import TabContent from './TabContent';
import { useViewMode } from 'src/hooks';
import BottomTabNavbar from './BottomTabNavbar';
import DesktopHeader from './DesktopHeader';
import SideTabNavbar from './SideTabNavbar';
import MobileHeader from './MobileHeader';
import { AnimatedFAB, FocusAwareStatusBar } from 'src/components';
import { useAppTheme } from 'src/providers/AppThemeProvider';
import MainMenuFab from 'src/components/MainMenuFab';
import { useRef } from 'react';
import { TourGuideHandle } from 'src/module/TourGuide/model';
import { isDefined } from 'src/utils';
import { IS_WEB, Margin, Size } from 'src/constants';
import { useBiometricsPrompt } from 'src/providers/BiometricsPromptProvider';
import {
  GuideElement,
  MobileGuideElement,
  STEPS,
  TourGuide,
  MobileTourGuide
} from 'src/scenes/Home/tour';
import styled from 'styled-components/native';
import { useTranslation } from 'react-i18next';
import { Plus } from 'react-native-feather';
import _ from 'lodash';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type TabNavigationConfig = {
  tabBarStyle: StyleProp<ViewStyle>;
};

type TabNavigationOptions = {
  title?: string;
};

type TabNavigationEventMap = {
  tabPress: {
    data: { isAlreadyFocused: boolean };
    canPreventDefault: true;
  };
};

type Props = DefaultNavigatorOptions<
  ParamListBase,
  TabNavigationState<ParamListBase>,
  TabNavigationOptions,
  TabNavigationEventMap
> &
  TabRouterOptions &
  TabNavigationConfig;

const CustomNavigator: React.FC<Props> = ({ initialRouteName, children, screenOptions }) => {
  const { state, navigation, descriptors } = useNavigationBuilder<
    TabNavigationState<ParamListBase>,
    TabRouterOptions,
    TabActionHelpers<ParamListBase>,
    TabNavigationOptions,
    TabNavigationEventMap
  >(TabRouter, {
    children,
    screenOptions,
    initialRouteName
  });

  const { t } = useTranslation('home');
  const { colors } = useAppTheme();
  const { bottom } = useSafeAreaInsets();

  const guideRef = useRef<TourGuideHandle>(null);
  const { needsPrompt } = useBiometricsPrompt();
  const { isMobile, isUnknown, windowHeight } = useViewMode();
  const [topTabHeight, setTopTabHeight] = React.useState(0);
  const [bottomTabHeight, setBottomTabHeight] = React.useState(0);

  const viewHeight = windowHeight - topTabHeight - bottomTabHeight;

  if (isUnknown) return <ActivityIndicator size='large' style={layout.flex1} />;

  if (isMobile)
    return (
      <MobileTourGuide.Provider
        ref={guideRef}
        disabled={!IS_WEB && (!isDefined(needsPrompt) || needsPrompt)}
      >
        <MobileTourGuide.Consumer>
          {({ enabled, exitTour }) => (
            <>
              <FocusAwareStatusBar backgroundColor={colors.surface} />
              <MobileHeader state={state} setTopTabHeight={setTopTabHeight} />
              <TabContent state={state} descriptors={descriptors} />
              <BottomTabNavbar
                state={state}
                navigation={navigation}
                setBottomTabHeight={setBottomTabHeight}
              />
              {!enabled && <MainMenuFab bottomTabHeight={bottomTabHeight} />}
              {enabled && (
                <FabContainer bottom={bottomTabHeight + Math.max(bottom, Size.M)}>
                  <MobileGuideElement
                    id={STEPS.FAB}
                    body={t('tour.fab')}
                    alignHorizontally
                    onContinue={exitTour}
                  >
                    <DummyFAB
                      icon={({ color, size }) => <Plus color={color} width={size} height={size} />}
                      label={''}
                      onPress={() => _.noop}
                    />
                  </MobileGuideElement>
                </FabContainer>
              )}
            </>
          )}
        </MobileTourGuide.Consumer>
      </MobileTourGuide.Provider>
    );

  return (
    <TourGuide.Provider
      ref={guideRef}
      disabled={!IS_WEB && (!isDefined(needsPrompt) || needsPrompt)}
    >
      <TourGuide.Consumer>
        {({ enabled, exitTour }) => (
          <>
            <DesktopHeader state={state} setTopTabHeight={setTopTabHeight} />
            <View style={[{ flexDirection: 'row', flex: 1 }, IS_WEB && { maxHeight: viewHeight }]}>
              <SideTabNavbar state={state} navigation={navigation} />
              <TabContent state={state} descriptors={descriptors} />
            </View>
            {!enabled && <MainMenuFab bottomTabHeight={bottomTabHeight} />}
            {enabled && (
              <FabContainer bottom={bottomTabHeight + Math.max(bottom, Size.M)}>
                <GuideElement
                  id={STEPS.FAB}
                  body={t('tour.fab')}
                  alignHorizontally
                  onContinue={exitTour}
                >
                  <DummyFAB
                    icon={({ color, size }) => <Plus color={color} width={size} height={size} />}
                    label={''}
                    onPress={() => _.noop}
                  />
                </GuideElement>
              </FabContainer>
            )}
          </>
        )}
      </TourGuide.Consumer>
    </TourGuide.Provider>
  );
};

export const createCustomNavigator = createNavigatorFactory(CustomNavigator);

const DummyFAB = styled(AnimatedFAB).attrs(({ theme }) => ({
  color: theme.colors.onPrimary,
  iconAnchor: 'right'
}))`
  background-color: ${({ theme }) => theme.colors.primary};
`;

const FabContainer = styled.View<{ bottom: number }>`
  position: absolute;
  bottom: ${({ bottom }) => bottom}px;
  right: ${Margin.Large}px;
`;
